import React from 'react';
import { Box, Typography, Alert, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Question } from '@aphp-ema/common/questionnaire';
import { getPreviewText } from '@/features/questionnaires/utils/conditionPreview';
import { useFormContext, useWatch } from 'react-hook-form';
import { QuestionnaireFormData } from '@/features/questionnaires/types';
import { useInitialQuestions } from '../../../hooks/useInitialQuestions';

interface ConditionPreviewProps {
  questionIndex: number;
  previousQuestions: Question[];
}

export const ConditionPreview: React.FC<ConditionPreviewProps> = React.memo(
  ({ questionIndex, previousQuestions }) => {
    const { t } = useTranslation();

    const { control } = useFormContext<QuestionnaireFormData>();
    const initialQuestions = useInitialQuestions();
    const displayConditions = useWatch({
      control,
      name: `questions.${questionIndex}.displayConditions`,
    });

    if (
      displayConditions === null ||
      displayConditions === undefined ||
      displayConditions?.conditions.length === 0
    ) {
      return null;
    }

    const { logicalOperator, conditions } = displayConditions;

    const validConditions = conditions.filter(
      (cond) => cond.target && cond.operator,
    );

    return (
      <>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('pages.studies.questions.conditions.preview')}:
        </Typography>
        <Alert severity="success">
          {validConditions.length === 0 ? (
            <Typography variant="body2">
              {t('pages.studies.questions.conditions.alwaysShow')}
            </Typography>
          ) : (
            <Box>
              {validConditions.map((condition, index) => {
                const text = getPreviewText(
                  condition,
                  previousQuestions,
                  initialQuestions,
                  t,
                );
                if (!text) return null;
                return (
                  <Box key={index}>
                    <Typography
                      variant="body2"
                      sx={{ mb: index < validConditions.length - 1 ? 1 : 0 }}
                    >
                      {text}
                    </Typography>
                    {index < validConditions.length - 1 && (
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          fontWeight: 'bold',
                          color: 'primary.main',
                          textAlign: 'center',
                        }}
                      >
                        {logicalOperator === 'AND'
                          ? t('pages.studies.questions.conditions.and')
                          : t('pages.studies.questions.conditions.or')}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Alert>
      </>
    );
  },
);
